// lib/source-defaults.ts
//
// Per-organisation default Source portfolio: the portfolio of the org's CURRENT ACTIVE CLIENT,
// where the Source tool should land when entering (nav click, back-to-dashboard). Without this
// the tool fell back to "top portfolio by asset count", which surfaced CARMILA over the active
// client ALTAREA.
//
// NOTE: kept as a code-level map on purpose. Organisation.portfolioId in the schema is an
// ACCESS-SCOPING field (it restricts portfolio-scoped users), NOT a default, do not reuse it.
// Promote this to a real Organisation-level setting when the schema next gains one (additive
// DDL via prisma/manual, never db push).
import type { ActiveEntityFocus, KernelInventoryState } from '#types/types';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';

const DEFAULT_SOURCE_PORTFOLIO_BY_ORG: Record<string, string> = {
  // Fater org -> ALTAREA (current active client). Add Unibail's org -> their portfolio when onboarded.
  '6803985b36d71277f7dbd438': 'cmku2t1co0051v9p4y9g9h4vj',
};

// Sentinel "portfolio id" used to open the Source editor with no portfolio
// selected yet. It is a non-empty string so it passes the kernel's
// `entityId` guards, while every render seam treats it as "no portfolio".
export const SOURCE_EMPTY_PORTFOLIO_ID = '__source_empty__';

export const resolveDefaultSourcePortfolioId = (organisationId?: string | null): string | null =>
  organisationId ? (DEFAULT_SOURCE_PORTFOLIO_BY_ORG[organisationId] ?? null) : null;

/** Entity types whose detail views belong to the Source wing: from these, the header's
 *  up-arrow navigates back to the Source dashboard (not the entity's table view). */
export const SOURCE_DOMAIN_ENTITY_TYPES = new Set<string>([
  'Portfolio',
  'Property',
  'Unit',
  'Brand',
  'PortfolioFit',
  'Contact',
  // Sourcing RESULTS + campaigns are the source wing too: after launching a sourcing you land on a
  // Lead, and from there the up-arrow must return to the Source dashboard. Omitting these
  // dead-ended the back-arrow on the 2026-07-22 Altarea call ("tu n'as pas accès au dashboard").
  'Lead',
  'SourcingCampaign',
  'PortfolioFitCampaign',
]);

/**
 * The portfolio a focused Source-wing entity BELONGS TO, read from the live inventory registry.
 * Returns null when the entity carries no portfolio link (e.g. Brand / Contact subjects) or when
 * the registry has not hydrated it yet; the caller decides on the fallback.
 */
export const resolveSubjectOwningPortfolioId = (
  subject: ActiveEntityFocus | null,
  inventory: KernelInventoryState | null | undefined,
): string | null => {
  if (!subject || !inventory) return null;
  const portfolioOfProperty = (propertyId?: string | null): string | null => {
    if (!propertyId) return null;
    const property = inventory.Property.registry[propertyId] as { portfolioId?: string } | undefined;
    return property?.portfolioId ?? null;
  };
  const { entityType, entityId } = subject;
  // The switch has a `default` branch, so an unlisted member is handled, not dropped. Spelling out
  // every entity type would be a list nobody maintains and the default would still be the thing doing the work.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (entityType) {
    case 'Portfolio':
      return entityId && entityId !== SOURCE_EMPTY_PORTFOLIO_ID ? entityId : null;
    case 'Property':
      return (inventory.Property.registry[entityId] as { portfolioId?: string } | undefined)?.portfolioId ?? null;
    case 'PortfolioFit':
      return (inventory.PortfolioFit.registry[entityId] as { portfolioId?: string } | undefined)?.portfolioId ?? null;
    // A unified campaign carries its own portfolioId at every granularity. Without this case
    // the switch fell through to the organisation fallback, so focusing a campaign from the
    // agent's chat list resolved the WRONG portfolio.
    case 'Campaign':
      return (inventory.Campaign.registry[entityId] as { portfolioId?: string } | undefined)?.portfolioId ?? null;
    case 'Unit':
      return portfolioOfProperty((inventory.Unit.registry[entityId] as { propertyId?: string } | undefined)?.propertyId);
    case 'Lead':
      return portfolioOfProperty((inventory.Lead.registry[entityId] as { propertyId?: string } | undefined)?.propertyId);
    case 'SourcingCampaign':
      return portfolioOfProperty(resolveCampaignPropertyId(readCampaignRow(inventory, entityId)));
    default:
      return null;
  }
};
